(() => {
  if (window.__lonoShippingProtectionController) return;
  window.__lonoShippingProtectionController = true;

  const configElement = document.querySelector('[data-lono-protection-config]');
  if (!configElement) return;
  const config = JSON.parse(configElement.textContent);
  const PROTECTION_VARIANT_IDS = new Set(config.variants.map(v => Number(v.id)));
  const TIERS = config.variants.map(v => {
    const match = v.title.match(/^Order up to US\$([\d,]+(?:\.\d{1,2})?)$/i);
    if (!match) throw new Error('Protection tier must be named Order up to US$155.');
    return {max: Math.round(Number(match[1].replaceAll(',', '')) * 100), variantId: Number(v.id), price: v.price, available: v.available};
  }).sort((a,b) => a.max - b.max);
  if (new Set(TIERS.map(t => t.max)).size !== TIERS.length) throw new Error('Duplicate protection limits.');
  let pending = 0;
  let latestCart;
  let cartReadAt = 0;
  let requestedChoice;
  let choiceVersion = 0;
  const ROOT = window.Shopify?.routes?.root || '/';
  let queue = Promise.resolve();

  const BASE_CURRENCY = 'USD';

  const getPresentmentRate = () => {
    const rate = Number(window.Shopify?.currency?.rate);

    if (config.baseCurrency !== 'USD') throw new Error('Protection limits require a USD base currency.');
    if (config.currency === 'USD') return 1;
    if (!Number.isFinite(rate) || rate <= 0 || window.Shopify?.currency?.active !== config.currency) throw new Error('Currency conversion is unavailable. Please reload.');
    return rate;
  };

  const getCart = async () => {
    const response = await fetch(`${ROOT}cart.js`, {
      headers: { Accept: 'application/json' }
    });

    if (!response.ok) throw new Error('Could not load the cart.');
    return response.json();
  };

  const isProtection = (item) =>
    Number(item.product_id) === config.productId || PROTECTION_VARIANT_IDS.has(Number(item.variant_id || item.id));

  const isEligibleItem = (item) =>
    !isProtection(item) &&
    !item.gift_card &&
    item.requires_shipping === true;

  const getEligibleSubtotal = (cart) =>
    cart.items
      .filter(isEligibleItem)
      .reduce((total, item) => total + item.final_line_price, 0);

  const getTier = (presentmentSubtotal) => {
    // Shopify cart values are in the customer's active market currency.
    // Tier boundaries are defined in your shop's USD base currency.
    const subtotalInUsd = Math.round(
      presentmentSubtotal / getPresentmentRate()
    );

    if (subtotalInUsd > 100000) return null;
    return (
      TIERS.find(
        (tier) => subtotalInUsd > 0 && subtotalInUsd <= tier.max
      ) || null
    );
  };

  const getProtectionItems = (cart) =>
    cart.items.filter(isProtection);

  const formatMoney = (cents, currency = 'USD') => {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency
      }).format(cents / 100);
    } catch {
      return `$${(cents / 100).toFixed(2)}`;
    }
  };

  const getSections = () => {
    const sectionIds = [];

    document
      .querySelectorAll('[data-lono-protection-section]')
      .forEach((element) => {
        const id = element.dataset.lonoProtectionSection;
        if (id) sectionIds.push(id);
      });

    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sectionIds } }));
    return [...new Set(sectionIds)].slice(0, 5);
  };

  const setBusy = (busy) => {
    document
      .querySelectorAll('[data-lono-protection-toggle]')
      .forEach((toggle) => {
        toggle.disabled = toggle.dataset.unavailable === 'true';
      });

    document
      .querySelectorAll('.cart-checkout-button, button[name="checkout"]')
      .forEach((button) => {
        if (busy) {
          if (!('lonoWasDisabled' in button.dataset)) button.dataset.lonoWasDisabled = String(button.disabled);
          button.disabled = true;
          button.setAttribute('aria-busy', 'true');
        } else {
          if (button.dataset.lonoWasDisabled === 'false') {
            button.disabled = false;
          }

          delete button.dataset.lonoWasDisabled;
          button.removeAttribute('aria-busy');
        }
      });
  };

  const setMessage = (message = '', type = '') => {
    document
      .querySelectorAll('[data-lono-protection-message]')
      .forEach((element) => {
        element.textContent = message;
        element.hidden = !message;
        element.dataset.status = type;
      });
  };

  const render = (cart) => {
    latestCart = cart;
    cartReadAt = Date.now();
    const subtotal = getEligibleSubtotal(cart);
    const tier = getTier(subtotal);
    const protectionItems = getProtectionItems(cart);

    const correctlyProtected =
      tier &&
      protectionItems.length === 1 &&
      Number(protectionItems[0].variant_id) === tier.variantId &&
      Number(protectionItems[0].quantity) === 1;

    document
      .querySelectorAll('[data-lono-shipping-protection]')
      .forEach((widget) => {
        const toggle = widget.querySelector('[data-lono-protection-toggle]');
        const price = widget.querySelector('[data-lono-protection-price]');
        const unavailable = widget.querySelector(
          '[data-lono-protection-unavailable]'
        );

        if (!toggle || !price || !unavailable) return;

        if (subtotal <= 0) {
          widget.hidden = true;
          return;
        }

        widget.hidden = false;

        if (!tier) {
          toggle.checked = false;
          toggle.disabled = true;
          toggle.dataset.unavailable = 'true';
          price.textContent = '';
          unavailable.hidden = false;
          unavailable.textContent =
            'Protection is available up to US$1,000.';
          return;
        }

        toggle.dataset.unavailable = String(!tier.available);
        toggle.disabled = !tier.available;
        toggle.checked = requestedChoice === undefined ? Boolean(correctlyProtected) : requestedChoice;
        const currentTierItem = protectionItems.find(
          (item) => Number(item.variant_id) === tier.variantId
        );

        const presentmentPrice = currentTierItem
          ? Math.round(
              currentTierItem.final_line_price /
              Math.max(currentTierItem.quantity, 1)
            )
          : tier.price;

        price.textContent = `${formatMoney(
          presentmentPrice,
          cart.currency || BASE_CURRENCY
        )}`;
        unavailable.hidden = true;
        unavailable.textContent = tier.available ? '' : 'This protection tier is temporarily unavailable.';
        unavailable.hidden = tier.available;
      });
  };

  const replaceMainCartSection = (cart) => {
    const current = document.querySelector('[data-lono-main-cart-section]');
    if (!current) return;

    const sectionId = current.dataset.lonoProtectionSection;
    const html = cart.sections?.[sectionId];
    if (!html) return;

    const documentFragment = new DOMParser().parseFromString(
      html,
      'text/html'
    );

    const replacement = documentFragment.querySelector(
      '[data-lono-main-cart-section]'
    );

    if (replacement) current.replaceWith(replacement);
  };

  const notifyTheme = (cart) => {
    replaceMainCartSection(cart);

    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', {
        bubbles: true,
        detail: {
          baseEvent: 'lono:shipping-protection',
          cart
        }
      })
    );
  };

  const updateProtection = async (cart, desiredTier) => {
    const protectionItems = getProtectionItems(cart);
    const updates = {};
    let retainedCorrectItem = false;

    protectionItems.forEach((item) => {
      const isCorrectVariant =
        desiredTier &&
        Number(item.variant_id) === desiredTier.variantId;

      if (isCorrectVariant && !retainedCorrectItem) {
        updates[item.key] = 1;
        retainedCorrectItem = true;
      } else {
        updates[item.key] = 0;
      }
    });

    if (desiredTier && !retainedCorrectItem) {
      updates[desiredTier.variantId] = 1;
    }

    if (!Object.keys(updates).length) return cart;

    const sections = getSections();

    const response = await fetch(`${ROOT}cart/update.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        updates,
        sections,
        sections_url: window.location.pathname
      })
    });

    if (!response.ok) {
      throw new Error('Could not update Shipping Protection.');
    }

    const updated = await response.json();
    if (sections.some(id => !updated.sections?.[id])) {
      const url = new URL(ROOT, window.location.origin);
      url.searchParams.set('sections', sections.join(','));
      const refreshed = await fetch(url, {headers: {Accept:'application/json'}});
      if (!refreshed.ok) throw new Error('Cart saved but display refresh failed. Please reload.');
      updated.sections = await refreshed.json();
      if (sections.some(id => !updated.sections?.[id])) throw new Error('Cart saved but display refresh failed. Please reload.');
    }
    return updated;
  };

  const reconcile = async (cart, customerChoice) => {
    const subtotal = getEligibleSubtotal(cart);
    const tier = getTier(subtotal);
    const protectionItems = getProtectionItems(cart);

    let desiredTier = null;

    if (customerChoice === true) {
      desiredTier = tier?.available ? tier : null;
    } else if (customerChoice === false) {
      desiredTier = null;
    } else if (protectionItems.length > 0) {
      // A protection line means the customer already selected it.
      // Keep that choice while moving it into the right tier.
      desiredTier = tier?.available ? tier : null;
    }

    const needsUpdate =
      (desiredTier &&
        (protectionItems.length !== 1 ||
          Number(protectionItems[0].variant_id) !== desiredTier.variantId ||
          Number(protectionItems[0].quantity) !== 1)) ||
      (!desiredTier && protectionItems.length > 0);

    if (!needsUpdate) {
      setMessage();
      render(cart);
      return cart;
    }

    const updatedCart = await updateProtection(cart, desiredTier);

    if (
      !tier &&
      subtotal > Math.round(100000 * getPresentmentRate())
    ) {
      setMessage('Protection is available up to US$1,000.', 'notice');
    } else {
      setMessage();
    }

    notifyTheme(updatedCart);
    render(updatedCart);

    return updatedCart;
  };

  const run = (task) => {
    pending++;
    setBusy(true);
    queue = queue
      .then(task)
      .catch((error) => {
        console.error('LONO Shipping Protection:', error);
        setMessage(
          'Shipping Protection could not be updated. Please try again.',
          'error'
        );
      })
      .finally(() => {
        pending--;
        if (latestCart) render(latestCart);
        setBusy(pending > 0);
      });

    return queue;
  };

  document.addEventListener('change', (event) => {
    const toggle = event.target.closest('[data-lono-protection-toggle]');
    if (!toggle) return;

    const requestedState = toggle.checked;
    requestedChoice = requestedState;
    const version = ++choiceVersion;
    if (latestCart) render(latestCart);

    setBusy(true);
    setMessage('Saving…', 'updating');

    run(async () => {
      if (version !== choiceVersion) return;
      const cart = latestCart && Date.now() - cartReadAt < 5000 ? latestCart : await getCart();
      try {
        await reconcile(cart, requestedState);
      } catch (error) {
        if (version === choiceVersion) {
          requestedChoice = undefined;
          render(await getCart());
        }
        throw error;
      } finally {
        if (version === choiceVersion) requestedChoice = undefined;
      }
    });
  });

  document.addEventListener('cart:change', (event) => {
    const cart = event.detail?.cart;

    if (!cart || event.detail?.baseEvent === 'lono:shipping-protection') {
      return;
    }

    cartReadAt = 0;
    setBusy(true);

    const eventCart = cart && Array.isArray(cart.items) && typeof cart.currency === 'string' ? cart : null;

    run(async () => {
      await reconcile(eventCart || await getCart(), requestedChoice);
    });
  });

  document.addEventListener('change', event => {
    if (event.target.matches('[data-line-key]')) cartReadAt = 0;
  }, true);

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-lono-protection-info]');
    if (!button) return;

    const widget = button.closest('[data-lono-shipping-protection]');
    const details = widget?.querySelector(
      '[data-lono-protection-details]'
    );

    if (!details) return;

    const expanded = button.getAttribute('aria-expanded') === 'true';

    button.setAttribute('aria-expanded', String(!expanded));
    details.hidden = expanded;
  });

  const initialise = () => run(async () => {
    await reconcile(await getCart());
  });
  if (!customElements.get('lono-cover')) {
    customElements.define('lono-cover', class extends HTMLElement {
      connectedCallback() {
        queueMicrotask(() => { if (latestCart) { render(latestCart); setBusy(pending > 0); } });
      }
    });
  }
  document.addEventListener('submit', event => {
    const form = event.target;
    const agreement = form.querySelector('#termsCheckbox, #finalSaleAgree');
    if (!form.querySelector('[name="checkout"]')) return;
    if (pending || (agreement && !agreement.checked)) {
      event.preventDefault();
      if (agreement && !agreement.checked) { agreement.focus(); agreement.closest('label').classList.add('highlight-error'); }
    }
  }, true);
  document.addEventListener('change', event => {
    if (event.target.matches('#termsCheckbox, #finalSaleAgree')) event.target.closest('label').classList.remove('highlight-error');
  });
  window.addEventListener('pageshow', event => { if (event.persisted) initialise(); });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialise, { once: true });
  } else {
    initialise();
  }
})();
